import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Checkbox, IconButton,
} from '@mui/material'
import { Add, Edit } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listNonconformityTypes, createNonconformityType, updateNonconformityType,
  type NonconformityType, type SaveNonconformityTypePayload,
} from '../../api/productieCalitate'
import { errorMessage } from '../../api/client'

const EMPTY: SaveNonconformityTypePayload = { code: '', description: '', isActive: true }

export default function NonconformityTypesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<NonconformityType | 'new' | null>(null)
  const [form, setForm] = useState<SaveNonconformityTypePayload>(EMPTY)
  const { data } = useQuery({ queryKey: ['productie', 'calitate', 'neconformitati'], queryFn: listNonconformityTypes, enabled: open })

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['productie', 'calitate', 'neconformitati'] })
  const createMutation = useMutation({ mutationFn: createNonconformityType, onSuccess: () => { invalidate(); setEditing(null) } })
  const updateMutation = useMutation({
    mutationFn: (v: { id: string; payload: SaveNonconformityTypePayload }) => updateNonconformityType(v.id, v.payload),
    onSuccess: () => { invalidate(); setEditing(null) },
  })
  const mutation = editing === 'new' ? createMutation : updateMutation
  const error = createMutation.error ?? updateMutation.error

  const startNew = () => { setForm(EMPTY); setEditing('new') }
  const startEdit = (t: NonconformityType) => { setForm({ code: t.code, description: t.description, isActive: t.isActive }); setEditing(t) }
  const save = () => {
    if (editing === 'new') createMutation.mutate(form)
    else if (editing) updateMutation.mutate({ id: editing.id, payload: form })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Catalog neconformități (FR-OQC-004)</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {editing ? (
            <Stack spacing={1.5}>
              <TextField label="Cod" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} fullWidth />
              <TextField label="Descriere" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline minRows={2} />
              <Stack direction="row" sx={{ alignItems: 'center' }}>
                <Checkbox checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} /> Activă
              </Stack>
              {error && <Alert severity="error">{errorMessage(error)}</Alert>}
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button onClick={() => setEditing(null)}>Renunță</Button>
                <Button variant="contained" onClick={save} disabled={mutation.isPending}
                  startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
                  Salvează
                </Button>
              </Stack>
            </Stack>
          ) : (
            <>
              <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                <Button size="small" startIcon={<Add />} onClick={startNew}>Neconformitate nouă</Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow><TableCell>Cod</TableCell><TableCell>Descriere</TableCell><TableCell align="center">Activă</TableCell><TableCell /></TableRow>
                </TableHead>
                <TableBody>
                  {data?.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell><code>{t.code}</code></TableCell>
                      <TableCell>{t.description}</TableCell>
                      <TableCell align="center">{t.isActive ? 'Da' : 'Nu'}</TableCell>
                      <TableCell align="right"><IconButton size="small" onClick={() => startEdit(t)}><Edit fontSize="small" /></IconButton></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}
